import { isAxiosError, isCancel } from 'axios';
import { NODON_NOT_FOUND } from '@shared/constants/router.constants';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  getErrorRecoveryMode,
  type ErrorRecoveryMode,
} from './error-boundary.helpers';

export type ResourceErrorNavigation = 'redirect' | 'wait';

/** Lookup finished and the URL project id is not in the org list. */
export const PROJECT_NOT_FOUND_MESSAGE = 'Project not found';

/** Lookup finished and the project has no usable version id. */
export const PROJECT_VERSION_NOT_FOUND_MESSAGE = 'Project version not found';

/**
 * Local only: retry a 404 this many times, then go to `/not-found`.
 * A local server that is down is not capped. Hosted apps do not retry.
 */
export const PRODUCTION_RESOURCE_LOAD_RETRY_LIMIT = 5;

const PERMANENT_NOT_FOUND_MESSAGES = new Set([
  'Invalid URL',
  'You are not a member of any organization',
  'You are not a member of this organization',
  'No organization found',
  'Missing required parameters',
  PROJECT_NOT_FOUND_MESSAGE,
  PROJECT_VERSION_NOT_FOUND_MESSAGE,
]);

/** Rate-limit / timeout only. 401/409/503 are handled by axios interceptors. */
const TRANSIENT_HTTP_STATUSES = new Set([408, 429]);

/**
 * True for browser/axios messages when nothing is listening (connection refused).
 * Exact match only — substring matching would change DataLoader wait-vs-redirect.
 */
const isNetworkUnavailableMessage = (message: string): boolean =>
  message === 'Failed to fetch' || message === 'Network Error';

/** Axios 503 is treated as maintenance and must not enter the DataLoader retry loop. */
const MAINTENANCE_HTTP_STATUS = 503;

/**
 * True for URL/membership/lookup failures that should stay on `/not-found`.
 * HTTP load failures (network, 404, 5xx) are not permanent — HMR and refetch can recover.
 */
export const isPermanentNotFoundMessage = (message: string): boolean =>
  PERMANENT_NOT_FOUND_MESSAGES.has(message);

/**
 * True when the HTTP/network error is likely brief (proxy down, HMR, 429).
 * Not transient: 401, 409 (version mismatch), 503 (maintenance), 404.
 */
export const isTransientResourceLoadError = (error: Error): boolean => {
  if (isCancel(error)) return true;
  if (isNetworkUnavailableMessage(error.message)) return true;
  if (!isAxiosError(error)) return false;

  const status = error.response?.status;
  if (status === undefined) return true;
  if (status === MAINTENANCE_HTTP_STATUS) return false;
  if (status >= 500) return true;
  return TRANSIENT_HTTP_STATUSES.has(status);
};

type ResourceErrorNavigationInput = {
  recoveryMode: ErrorRecoveryMode;
  error: Error;
  hasUsableCachedResource: boolean;
  /**
   * How many local 404 retries have already run.
   * Ignored when the local server is unreachable, and ignored in hosted apps.
   */
  productionRetryCount?: number;
};

/**
 * The API answered, and the resource is not there.
 * Connection refused and 5xx are a down server, not a missing project.
 */
const isMissingResourceResponse = (error: Error): boolean =>
  isAxiosError(error) && error.response?.status === 404;

/**
 * Stay on the current route while a local retry can still recover the load.
 * Hosted apps redirect immediately. Permanent lookup errors do too.
 * Locally, a 404 is capped. A down server stays on the loader.
 */
export const decideResourceErrorNavigation = ({
  recoveryMode,
  error,
  hasUsableCachedResource,
  productionRetryCount = 0,
}: ResourceErrorNavigationInput): ResourceErrorNavigation => {
  if (hasUsableCachedResource) return 'wait';
  if (isPermanentNotFoundMessage(error.message)) return 'redirect';
  if (recoveryMode !== 'hmr') return 'redirect';
  // Branch switch takes the API down. That must not become `/not-found`.
  if (!isMissingResourceResponse(error)) return 'wait';
  const retryExhausted =
    productionRetryCount >= PRODUCTION_RESOURCE_LOAD_RETRY_LIMIT;
  return retryExhausted ? 'redirect' : 'wait';
};

/**
 * Keep an already-open project when a refetch fails.
 * Only during Vite HMR — hosted apps must still 404 if the project is gone.
 */
export const shouldPreserveLoadedProjectOnRefetchFailure = (
  recoveryMode: ErrorRecoveryMode = getErrorRecoveryMode(),
): boolean => recoveryMode === 'hmr';

export type ResourceLoadRetryAction =
  | { type: 'none' }
  | { type: 'project'; organization: string; projectId: number }
  | { type: 'projects'; organization: string }
  | { type: 'shared'; sharingKey: string }
  | { type: 'generation'; generationKey: string };

export type ResourceLoadRetryRoute =
  | { type: 'project'; organization: string; projectId: string }
  | { type: 'project-list' }
  | { type: 'shared'; sharingKey: string }
  | { type: 'generation'; generationKey: string }
  | {
      type: 'error';
      raw:
        | { type: 'project'; organization?: string }
        | { type: 'shared'; sharingKey?: string }
        | { type: 'generation'; generationKey?: string };
    };

/**
 * What to refetch while waiting on a load error.
 * {@link ResourceLoadRetryAction} `none` means production must not wait.
 */
export const getResourceLoadRetryAction = (
  route: ResourceLoadRetryRoute,
): ResourceLoadRetryAction => {
  switch (route.type) {
    case 'project': {
      const projectId = Number(route.projectId);
      if (!Number.isFinite(projectId)) return { type: 'none' };
      return {
        type: 'project',
        organization: route.organization,
        projectId,
      };
    }
    case 'project-list':
      return { type: 'none' };
    case 'shared':
      if (!route.sharingKey) return { type: 'none' };
      return { type: 'shared', sharingKey: route.sharingKey };
    case 'generation':
      if (!route.generationKey) return { type: 'none' };
      return { type: 'generation', generationKey: route.generationKey };
    case 'error':
      return getErrorRouteRetryAction(route.raw);
    default:
      return assertUnreachable(route);
  }
};

const getErrorRouteRetryAction = (
  raw: Extract<ResourceLoadRetryRoute, { type: 'error' }>['raw'],
): ResourceLoadRetryAction => {
  switch (raw.type) {
    case 'project':
      if (!raw.organization) return { type: 'none' };
      return { type: 'projects', organization: raw.organization };
    case 'shared':
      if (!raw.sharingKey) return { type: 'none' };
      return { type: 'shared', sharingKey: raw.sharingKey };
    case 'generation':
      if (!raw.generationKey) return { type: 'none' };
      return { type: 'generation', generationKey: raw.generationKey };
    default:
      return assertUnreachable(raw);
  }
};

/**
 * Server 404 JSON is `{ error: "Resource could not be found" }`; axios uses its own message.
 */
export const getResourceLoadErrorMessage = (error: Error): string => {
  if (!isAxiosError(error)) return error.message;
  const body = error.response?.data;
  if (!guards.is.object(body) || !('error' in body)) return error.message;
  if (!guards.is.string(body.error)) return error.message;
  return body.error;
};

export type ResourceRetryBudget = {
  count: number;
  trackedFailure: string;
};

/**
 * Stable id for one failed resource. An empty message means no current failure.
 */
export const resourceLoadFailureIdentity = (
  route: ResourceLoadRetryRoute,
  message: string,
): string => `${route.type}:${message}:${JSON.stringify(route)}`;

/**
 * Keep the retry count while a refetch briefly clears the error.
 * A new failure, or a load that finished, starts the budget over.
 */
export const nextResourceRetryBudget = (
  current: ResourceRetryBudget,
  input: { failureIdentity: string; isRetryInFlight: boolean },
): ResourceRetryBudget => {
  if (!input.failureIdentity) {
    if (input.isRetryInFlight) return current;
    if (current.count === 0 && current.trackedFailure === '') return current;
    return { count: 0, trackedFailure: '' };
  }
  if (input.failureIdentity === current.trackedFailure) return current;
  return { count: 0, trackedFailure: input.failureIdentity };
};

/**
 * `/not-found` URL including the failed route, shown on the 404 page.
 */
export const buildNotFoundHref = (
  pathname: string,
  search: string,
  error: Error,
): string => {
  const searchParams = new URLSearchParams({
    message: getResourceLoadErrorMessage(error),
    path: pathname,
  });
  if (search) searchParams.set('returnSearch', search);
  return `${NODON_NOT_FOUND}?${searchParams.toString()}`;
};
